import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { ApiError, api } from '@/lib/api';
import ExtracurricularSlotEditModal from './ExtracurricularSlotEditModal';

/**
 * Конкретная сессия внеурочного занятия (slot + date).
 *
 * Сессия в БД создаётся лениво: первый PUT по паре (slot_id, date)
 * её и заводит. До этого можно открыть страницу — будут показаны
 * шапка и пустой состав, без id сессии. Как только пользователь
 * меняет тему / отмечает «занятие проведено» / добавляет ученика —
 * запись в БД создаётся.
 */
function fmtDateRu(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

export default function ExtracurricularSessionPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite = can('extracurricular.schedule', 'write');
  const navigate = useNavigate();
  const [search] = useSearchParams();

  const slotId = parseInt(search.get('slot') || '0', 10);
  const date   = search.get('date') || '';

  const [slot, setSlot]         = useState(null);
  const [session, setSession]   = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoad]      = useState(true);
  const [error, setError]       = useState(null);

  // Локальные сохраняемые значения формы (с debounce-сейвом).
  const [theme, setTheme]         = useState('');
  const [conducted, setConducted] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  // Открыта ли модалка редактирования слота.
  const [editSlot, setEditSlot] = useState(false);
  // Подавление лишних PUT при первичной загрузке.
  const initialLoaded = useRef(false);

  const reload = () => {
    if (!slotId || !date) return;
    setLoad(true); setError(null);
    api.get(`/api/extracurricular-sessions/by-slot-date?slot_id=${slotId}&date=${date}`)
      .then((res) => {
        setSlot(res.slot);
        setSession(res.session);
        setStudents(res.students || []);
        setTheme(res.session?.theme || '');
        setConducted(!!res.session?.conducted);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => {
        setLoad(false);
        // Чуть откладываем разрешение автосейва, чтобы первый useEffect
        // от setTheme/setConducted не дёрнул PUT сразу после получения.
        setTimeout(() => { initialLoaded.current = true; }, 100);
      });
  };
  useEffect(reload, [slotId, date]);

  // Авто-сохранение шапки (тема + проведено) с дебаунсом.
  useEffect(() => {
    if (!initialLoaded.current) return;
    if (!canWrite) return;
    const t = setTimeout(() => {
      saveMeta();
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, conducted]);

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const res = await api.put(
        `/api/extracurricular-sessions/by-slot-date?slot_id=${slotId}&date=${date}`,
        { theme, conducted }
      );
      setSession(res.session);
      // Состав вернётся из бэка тот же — если сессии не было, он пустой
      if (Array.isArray(res.students)) setStudents(res.students);
    } catch (e) {
      // Тихая ошибка — не дёргаем пользователя; ошибки видны в консоли.
      console.error('saveMeta failed', e);
    } finally {
      setSavingMeta(false);
    }
  };

  const ensureSession = async () => {
    if (session) return session;
    // Создаём сессию через тот же PUT — пустой PATCH создаст запись.
    const res = await api.put(
      `/api/extracurricular-sessions/by-slot-date?slot_id=${slotId}&date=${date}`,
      { theme, conducted }
    );
    setSession(res.session);
    return res.session;
  };

  const onAddStudent = async (studentId) => {
    const s = await ensureSession();
    if (!s?.id) return;
    try {
      const res = await api.post(`/api/extracurricular-sessions/${s.id}/students`, { studentId });
      setStudents(res.students || []);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось добавить');
    }
  };

  const onUpdateStudent = async (studentId, patch) => {
    if (!session?.id) return;
    try {
      const res = await api.put(`/api/extracurricular-sessions/${session.id}/students/${studentId}`, patch);
      setStudents(res.students || []);
    } catch (e) {
      console.error(e);
    }
  };

  const onDeleteStudent = async (studentId) => {
    if (!session?.id) return;
    if (!confirm('Убрать ученика из этого занятия?')) return;
    try {
      const res = await api.delete(`/api/extracurricular-sessions/${session.id}/students/${studentId}`);
      setStudents(res.students || []);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось');
    }
  };

  const back = () => navigate('/extracurricular/schedule');

  // Заголовок: «Reading club (06.05.2026) Зеленова Анастасия Алексеевна»
  const title = useMemo(() => {
    if (!slot) return 'Внеурочное занятие';
    return `${slot.subjectName || '—'} (${fmtDateRu(date)}) ${slot.teacherName || ''}`.trim();
  }, [slot, date]);

  if (!slotId || !date) {
    return (
      <>
        <Topbar breadcrumb={[{ label: 'Внеурочные занятия', to: '/extracurricular/schedule' }, 'Сессия']}
                density={tweaks.density} onDensityChange={(d) => updateTweak('density', d)} />
        <div className="ref-empty" style={{ padding: 32 }}>
          Не указаны параметры (slot и date).
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        breadcrumb={[{ label: 'Внеурочные занятия', to: '/extracurricular/schedule' }, 'Расписание', title]}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="filterbar">
        <div className="filterbar__row" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn--sm" onClick={back}>
            ‹ К расписанию
          </button>
          <span style={{ fontWeight: 600, marginLeft: 6 }}>{title}</span>
          {canWrite && slot && (
            <button className="btn btn--ghost btn--sm" onClick={() => setEditSlot(true)} title="Редактировать слот">
              <Icon name="edit" size={12} />
            </button>
          )}
          {savingMeta && (
            <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>сохраняем…</span>
          )}
          <div style={{ marginLeft: 'auto' }}>
            {canWrite && session?.id && (
              <AddStudentButton onAdd={onAddStudent} excludeIds={students.map((s) => s.studentId)} />
            )}
            {canWrite && !session?.id && (
              <AddStudentButton onAdd={onAddStudent} excludeIds={[]} disabled={!canWrite}
                                hint="Сессия будет создана автоматически" />
            )}
          </div>
        </div>
      </div>

      <div className="main-content-wrap" style={{ padding: 16 }}>
        {error && <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>}
        {loading && <div className="muted" style={{ padding: 16 }}>Загрузка…</div>}

        {!loading && (
          <>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <label className="ref-checkbox" style={{ fontSize: 14 }}>
                <input type="checkbox" checked={conducted}
                       onChange={(e) => setConducted(e.target.checked)}
                       disabled={!canWrite} />
                <span style={{ fontWeight: 600 }}>Занятие проведено</span>
              </label>
              {session?.conductedAt && (
                <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  отмечено {session.conductedAt.replace('T', ' ').slice(0, 16)}
                  {session.conductedByName ? ` — ${session.conductedByName}` : ''}
                </span>
              )}
            </div>

            <div style={{ marginBottom: 24 }}>
              <label className="sd-edit__label">Тема занятия</label>
              <input
                className="sd-edit__input"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                placeholder="о чём было занятие"
                disabled={!canWrite}
                maxLength={500}
                style={{ width: '100%', maxWidth: 720 }}
              />
            </div>

            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>ФИО</th>
                    <th style={{ width: '50%' }}>Оценка / комментарий</th>
                    {canWrite && <th style={{ width: 50 }} />}
                  </tr>
                </thead>
                <tbody>
                  {students.length === 0 ? (
                    <tr>
                      <td colSpan={canWrite ? 3 : 2} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                        Учеников ещё нет. Нажмите «+ добавить ученика» в шапке.
                      </td>
                    </tr>
                  ) : students.map((s) => (
                    <StudentRow
                      key={s.studentId}
                      st={s}
                      canWrite={canWrite}
                      onUpdate={(patch) => onUpdateStudent(s.studentId, patch)}
                      onDelete={() => onDeleteStudent(s.studentId)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {editSlot && slot && (
        <ExtracurricularSlotEditModal
          mode="edit"
          slotId={slot.id}
          onClose={() => setEditSlot(false)}
          onSaved={() => { setEditSlot(false); reload(); }}
          onDeleted={() => navigate('/extracurricular/schedule')}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Строка ученика с двумя редактируемыми полями (оценка / комментарий) */
/* ------------------------------------------------------------------ */
function StudentRow({ st, canWrite, onUpdate, onDelete }) {
  const [score, setScore]     = useState(st.score || '');
  const [comment, setComment] = useState(st.comment || '');
  const initial = useRef(true);

  useEffect(() => { setScore(st.score || ''); setComment(st.comment || ''); }, [st.studentId]);

  // Дебаунс-сохранение полей.
  useEffect(() => {
    if (initial.current) { initial.current = false; return; }
    if (!canWrite) return;
    const t = setTimeout(() => {
      onUpdate({ score, comment });
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [score, comment]);

  return (
    <tr>
      <td>{st.studentName}{st.studentStatus === 'archive' && (
        <span className="pay-st pay-st--mute" style={{ marginLeft: 6, fontSize: 10.5 }}>архив</span>
      )}</td>
      <td>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            value={score}
            onChange={(e) => setScore(e.target.value)}
            placeholder="оценка"
            className="sd-edit__input"
            style={{ width: 70 }}
            disabled={!canWrite}
            maxLength={20}
          />
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="комментарий"
            className="sd-edit__input"
            style={{ flex: 1 }}
            disabled={!canWrite}
            maxLength={500}
          />
        </div>
      </td>
      {canWrite && (
        <td>
          <button className="btn btn--ghost btn--sm" style={{ color: 'var(--red-fg)' }}
                  title="Убрать из занятия" onClick={onDelete}>
            <Icon name="x" size={12} />
          </button>
        </td>
      )}
    </tr>
  );
}

/* ------------------------------------------------------------------ */
/* Кнопка «+ добавить ученика» с поиском по справочнику                */
/* ------------------------------------------------------------------ */
function AddStudentButton({ onAdd, excludeIds, disabled, hint }) {
  const [open, setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const [rows, setRows]   = useState([]);
  const [loading, setLoad] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoad(true);
    const t = setTimeout(() => {
      // Скоупнутая ручка внеурочки вместо общего /api/students — работает и
      // для координатора без права students.read (id + ФИО + класс).
      const qs = new URLSearchParams({ limit: '300' });
      if (search.trim()) qs.set('q', search.trim());
      api.get('/api/extracurricular/student-search?' + qs.toString())
        .then((res) => {
          if (cancelled) return;
          const set = new Set(excludeIds || []);
          setRows((res.rows || []).filter((s) => !set.has(s.id)));
        })
        .catch(() => { if (!cancelled) setRows([]); })
        .finally(() => { if (!cancelled) setLoad(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, search]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="btn btn--primary btn--sm"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title={hint}
      >
        <Icon name="plus" size={12} /> добавить ученика
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '110%', zIndex: 50,
          width: 360, maxHeight: 380, overflow: 'hidden',
          background: 'var(--panel)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          display: 'flex', flexDirection: 'column',
        }}>
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск ученика…"
            style={{
              padding: '8px 10px', border: 'none', borderBottom: '1px solid var(--border)',
              background: 'transparent', color: 'var(--fg)', outline: 'none', fontSize: 13,
            }}
          />
          <div style={{ overflow: 'auto' }}>
            {loading && rows.length === 0 && (
              <div className="muted" style={{ padding: 12, fontSize: 12 }}>Загрузка…</div>
            )}
            {!loading && rows.length === 0 && (
              <div className="muted" style={{ padding: 12, fontSize: 12 }}>
                {search ? 'Ничего не найдено' : 'Нет учеников'}
              </div>
            )}
            {rows.map((s) => (
              <button
                key={s.id} type="button"
                onClick={() => { onAdd(s.id); setOpen(false); setSearch(''); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 10px', border: 'none', background: 'transparent',
                  cursor: 'pointer', fontSize: 13, color: 'var(--fg)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--panel-2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {[s.familyName, s.firstName, s.midName].filter(Boolean).join(' ')}
                {s.class?.label && <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>{s.class.label}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
